import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { motion, useDragControls, useMotionValue, useReducedMotion } from 'framer-motion'
import { GripVertical, Menu, PanelRight, SquarePen, X } from 'lucide-react'
import { cn } from '@/lib/utils'
import { useChatStore } from '@/stores/chat-store'
import { useThreadStore } from '@/stores/thread-store'
import { CompanionComposer } from './companion-composer'
import { ChatConversationBody, ChatActionBanners } from './chat-conversation-body'
import { CompanionQuickActions } from './companion-quick-actions'
import { CompanionHistoryPanel } from './companion-history-panel'
import { useChatTickerDrop } from './use-chat-ticker-drop'
import { TickerDropOverlay } from '@/features/ticker-drag/ticker-drop-overlay'
import type { CompanionConversation } from './use-companion-conversation'

export const FLOATING_WINDOW_WIDTH = 340
const DEFAULT_MARGIN = 24
const DEFAULT_TOP = 64

/**
 * Zoom origin for the open/minimize morph: the launcher button the window
 * "comes from". Resolved live because the launcher sits in a different spot
 * per page header (and the hideChrome backtest editor places it elsewhere).
 */
function launcherOrigin(left: number, top: number): string {
  const el = document.querySelector('[data-companion-launcher]')
  if (!el) return 'top right'
  const rect = el.getBoundingClientRect()
  const ox = rect.left + rect.width / 2 - left
  const oy = rect.top + rect.height / 2 - top
  return `${ox}px ${oy}px`
}

function defaultPosition(): { x: number; y: number } {
  return {
    x: Math.max(DEFAULT_MARGIN, window.innerWidth - FLOATING_WINDOW_WIDTH - DEFAULT_MARGIN),
    y: DEFAULT_TOP,
  }
}

/** Clamp a restored position so an off-screen window is always recoverable. */
function clampToViewport(pos: { x: number; y: number }): { x: number; y: number } {
  return {
    x: Math.min(Math.max(0, pos.x), Math.max(0, window.innerWidth - 80)),
    y: Math.min(Math.max(0, pos.y), Math.max(0, window.innerHeight - 80)),
  }
}

/**
 * Presses that must NOT start a window drag: form controls, links, and
 * anything else clickable. Everything else in the window drags it.
 */
const NO_DRAG_SELECTOR = 'textarea, input, select, button, a, [role="button"], [role="menuitem"]'

/** True when the press landed on an element's own scrollbar (not its content). */
function isScrollbarPress(e: React.PointerEvent): boolean {
  const el = e.target as HTMLElement
  const rect = el.getBoundingClientRect()
  return (
    (el.clientWidth > 0 && e.clientX - rect.left >= el.clientWidth) ||
    (el.clientHeight > 0 && e.clientY - rect.top >= el.clientHeight)
  )
}

const HEADER_BUTTON =
  'flex h-6 w-6 shrink-0 items-center justify-center rounded text-muted-foreground hover:bg-hover hover:text-foreground'

interface CompanionFloatingWindowProps {
  conversation: CompanionConversation
}

/**
 * The floating companion — a Spotlight-style COMMAND BAR, deliberately
 * minimal on open (per Chris 2026-08-22, modeled on the backtest inline
 * composer): a slim draggable bar with the ask input. Quick actions appear
 * only while typing, as a filtered dropdown. Once a conversation starts, the
 * answer expands DOWNWARD below the bar into a fixed-height scrolling panel.
 *
 * Expanded, a slim header carries history + New chat (left) and pin + close
 * (right). Pressing anywhere non-interactive drags the window (Chris,
 * 2026-09-30): moving it aside beats text selection here, so assistant
 * messages carry a Copy button instead.
 *
 * It overlays the workspace (never interacts with the mosaic), zooms into
 * the shared launcher on minimize, and pins to the side drawer. The
 * conversation itself lives in useCompanionConversation, mounted once by
 * CompanionHost.
 */
export function CompanionFloatingWindow({ conversation }: CompanionFloatingWindowProps) {
  const setOpen = useChatStore((s) => s.setOpen)
  const setSurface = useChatStore((s) => s.setSurface)
  const storedPosition = useChatStore((s) => s.floatingPosition)
  const setFloatingPosition = useChatStore((s) => s.setFloatingPosition)
  const draft = useChatStore((s) => s.draft)
  const setDraft = useChatStore((s) => s.setDraft)
  const setHistoryOpen = useThreadStore((s) => s.setHistoryOpen)
  const historyOpen = useThreadStore((s) => s.historyOpen)
  const reducedMotion = useReducedMotion()
  const tickerDrop = useChatTickerDrop()
  const dragControls = useDragControls()
  // Drag deltas accumulate in motion values; committed to the store (as
  // left/top) on drag end, then reset — so left/top stay the single source
  // of truth and persistence stays one small key.
  const dragX = useMotionValue(0)
  const dragY = useMotionValue(0)
  // Mirrors the ask bar's text: non-empty text surfaces the quick actions.
  const [inputValue, setInputValue] = useState('')
  // Drag-vs-click on the pill: a press that MOVED is a drag; a plain click
  // focuses the input instead.
  const barRef = useRef<HTMLDivElement>(null)
  const didDragRef = useRef(false)
  // Fresh-on-reopen (per Chris 2026-08-22, Google style): each OPEN of the
  // floating window starts visually fresh — only messages sent from THIS
  // open are shown. The full history stays in the drawer. Until the user
  // sends here, the watermark fast-forwards past anything already in (or
  // rehydrating into) the shared stream.
  const [startIndex, setStartIndex] = useState(conversation.messages.length)
  const interactedRef = useRef(false)
  useEffect(() => {
    if (!interactedRef.current && conversation.messages.length !== startIndex) {
      setStartIndex(conversation.messages.length)
    }
  }, [conversation.messages.length, startIndex])
  const handleSend = useCallback(
    (text: string) => {
      interactedRef.current = true
      conversation.handleSend(text)
    },
    [conversation],
  )
  // New chat / a picked thread: the whole thread is THIS window's content,
  // so the fresh-on-reopen watermark drops to zero. A new chat then has no
  // messages and collapses back to the bare command bar.
  const { handleNewConversation, handleSelectConversation } = conversation
  const handleNewThread = useCallback(() => {
    interactedRef.current = true
    setStartIndex(0)
    handleNewConversation()
  }, [handleNewConversation])
  const handleSelectThread = useCallback(
    (id: string) => {
      interactedRef.current = true
      setStartIndex(0)
      handleSelectConversation(id)
    },
    [handleSelectConversation],
  )
  const position = useMemo(
    () => clampToViewport(storedPosition ?? defaultPosition()),
    [storedPosition],
  )
  // Recomputed whenever the window moves so the open AND minimize morphs
  // always aim at the launcher's live location (a mount-time snapshot went
  // stale after drags and read as a plain fade).
  const origin = useMemo(() => launcherOrigin(position.x, position.y), [position.x, position.y])

  const hasConversation =
    interactedRef.current &&
    (conversation.messages.length > startIndex ||
      conversation.streamingText !== '' ||
      conversation.isProcessing)

  // ── ESC minimizes ────────────────────────────────────────
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false)
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [setOpen])

  const commitDrag = useCallback(() => {
    const next = clampToViewport({
      x: position.x + dragX.get(),
      y: position.y + dragY.get(),
    })
    setFloatingPosition(next)
    dragX.set(0)
    dragY.set(0)
  }, [dragX, dragY, position.x, position.y, setFloatingPosition])

  return (
    <motion.div
      role="dialog"
      aria-label="Edge AI"
      data-testid="companion-floating-window"
      data-companion-surface
      className={cn(
        'fixed z-[70] flex flex-col overflow-hidden rounded-lg border border-border bg-background shadow-2xl',
        // Drag-anywhere wins over text selection; the input stays editable.
        hasConversation && 'select-none [&_textarea]:select-text',
      )}
      style={{
        left: position.x,
        top: position.y,
        width: FLOATING_WINDOW_WIDTH,
        // A DEFINITE height once the conversation exists — the body's flex
        // scroll chain (flex-1 min-h-0 + h-full) needs one to resolve
        // against. The compact command bar stays auto-height.
        // History opened from the compact bar needs the room too.
        height: hasConversation || historyOpen ? '55vh' : 'auto',
        x: dragX,
        y: dragY,
        transformOrigin: origin,
      }}
      drag
      dragListener={false}
      dragControls={dragControls}
      dragMomentum={false}
      onDragStart={() => {
        didDragRef.current = true
      }}
      onDragEnd={commitDrag}
      onPointerDown={(e) => {
        // Drag from anywhere non-interactive (the top bar and the ask bar
        // handle their own presses).
        if (e.button !== 0 || !hasConversation) return
        const target = e.target as HTMLElement
        if (
          target.closest(NO_DRAG_SELECTOR) ||
          target.closest('[data-testid="floating-title-bar"]')
        )
          return
        if (barRef.current?.contains(target)) return
        if (isScrollbarPress(e)) return
        dragControls.start(e)
      }}
      {...tickerDrop.dropHandlers}
      initial={reducedMotion ? { opacity: 0 } : { opacity: 0, scale: 0.2 }}
      animate={reducedMotion ? { opacity: 1 } : { opacity: 1, scale: 1 }}
      exit={reducedMotion ? { opacity: 0 } : { opacity: 0, scale: 0.1 }}
      transition={{ type: 'spring', duration: 0.35, bounce: 0.15 }}
    >
      <TickerDropOverlay
        isDragging={tickerDrop.isDragging}
        isOver={tickerDrop.isOver}
        label={`Ask about $${tickerDrop.ticker ?? ''}`}
        className="rounded-lg"
      />
      {/* Layout (Chris, 2026-10-02): the TOP BAR is always shown and is the
          window's handle: grip + history + New chat left, wordmark centered,
          pin + close right, so a first-time user sees what this is and how to
          close or dock it. Compact = top bar + ask bar. Expanded =
          conversation between them. Pressing anywhere non-interactive on the
          expanded window also drags it. */}
      {/* History slide-over, scoped to the window like the drawer's. */}
      <CompanionHistoryPanel onSelectThread={handleSelectThread} onNewThread={handleNewThread} />
      {/* 1fr|auto|1fr so the wordmark stays centered, matching the drawer. */}
      <div
        data-testid="floating-title-bar"
        className="grid h-8 shrink-0 cursor-grab grid-cols-[1fr_auto_1fr] items-center border-b border-border px-1 select-none active:cursor-grabbing"
        onPointerDown={(e) => {
          if (e.button !== 0) return
          if ((e.target as HTMLElement).closest(NO_DRAG_SELECTOR)) return
          dragControls.start(e)
        }}
      >
        <div className="flex items-center gap-0.5">
          <span
            className="flex h-6 w-4 shrink-0 items-center justify-center text-muted-foreground"
            aria-label="Drag window"
            title="Drag to move"
          >
            <GripVertical size={13} />
          </span>
          <button
            onClick={() => setHistoryOpen(true)}
            className={HEADER_BUTTON}
            title="Conversations"
            aria-label="Conversation history"
          >
            <Menu size={14} />
          </button>
          <button
            onClick={handleNewThread}
            className={HEADER_BUTTON}
            title="New chat"
            aria-label="New chat"
          >
            <SquarePen size={13} />
          </button>
        </div>
        {/* Same wordmark + Beta pill as the drawer header. */}
        <span className="flex items-center gap-1.5">
          <span className="text-xs font-semibold text-foreground">Edge AI</span>
          <span className="rounded-sm bg-primary px-1 py-px text-[8px] font-semibold uppercase leading-none tracking-wide text-primary-foreground">
            Beta
          </span>
        </span>
        <div className="flex items-center justify-end gap-0.5">
          <button
            onClick={() => setSurface('drawer')}
            className={HEADER_BUTTON}
            title="Pin to side panel"
            aria-label="Pin to side"
          >
            <PanelRight size={14} />
          </button>
          <button
            onClick={() => setOpen(false)}
            className={HEADER_BUTTON}
            title="Close"
            aria-label="Close chat"
          >
            <X size={14} />
          </button>
        </div>
      </div>
      {hasConversation && (
        <>
          <ChatConversationBody
            conversation={conversation}
            visible
            emptyState={null}
            startIndex={startIndex}
          />
          <ChatActionBanners />
        </>
      )}

      <div
        ref={barRef}
        className="flex shrink-0 cursor-text items-center gap-0.5 px-0.5 py-0.5"
        onPointerDown={(e) => {
          const target = e.target as HTMLElement
          if (target.closest('textarea, input, button')) return
          didDragRef.current = false
          dragControls.start(e)
        }}
        onClick={(e) => {
          const target = e.target as HTMLElement
          if (target.closest('textarea, input, button')) return
          if (!didDragRef.current) barRef.current?.querySelector('textarea')?.focus()
        }}
      >
        {/* The unified composer — its controls row carries the `+` menu and
            effort chip. Grip and pin live on the top bar. */}
        <div className="min-w-0 flex-1">
          <CompanionComposer
            variant="floating"
            onSend={handleSend}
            disabled={conversation.isProcessing}
            placeholder="Ask Edge AI, or / for actions"
            seedText={draft}
            onSeedConsumed={() => setDraft(null)}
            onValueChange={setInputValue}
          />
        </div>
      </div>

      {/* Quick actions — keyboard-first: typing filters while fresh, and a
          leading "/" browses anytime. */}
      {(inputValue.trimStart().startsWith('/') ||
        (!hasConversation && inputValue.trim() !== '')) && (
        <CompanionQuickActions filter={inputValue.trimStart().replace(/^\//, '')} />
      )}
    </motion.div>
  )
}
